import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {RouterLink} from '@angular/router';
import {Contract, isContractActive} from '@shared/api/models/query/contract.model';
import {ContractLinkedOrganisation} from '@shared/api/models/query/organisation.model';
import {ContractsService} from '@shared/api/services/v2/contracts.service';
import {Pagination} from '@models/pagination.model';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {organisationDetailRoute} from '@shared/services/router/organisation-routes';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {LinkedRecordsTabBase} from '@shared/components/linked-records/linked-records-tab.base';
import {LinkRecordsModalConfig} from '@shared/components/linked-records/link-records.modal';

/** Safety bound on the pages read for the link popup. */
const MAX_CANDIDATE_PAGES = 100;

/**
 * UNIVAPLAT-3700: Organisations tab of the contract detail page. The page renders it for holders
 * of VIEW_CONTRACT_ORGANISATIONS (general admins and the contract's admins).
 *
 * - Lists the organisations linked to the contract (`linked=true` answers a flat `{data}` with no
 *   pagination). Link and unlink need MANAGE_CONTRACT_ORGANISATIONS and an active contract.
 * - The API answers 422 on `linked=false` for a deactivated or deleted contract, so the link popup
 *   is never offered (and that call never made) while the contract is inactive.
 * - The link popup reads every page of `linked=false` (active organisations not yet linked).
 * - Names link to the organisation details only for holders of VIEW_ORGANISATIONS.
 * - `changed` (from the base) lets the page refresh the contract for the delete guard.
 * - Backend owns the error text. Organisation data is never logged and is dropped on destroy.
 */
@Component({
    selector: 'cdss-contract-organisations-tab',
    standalone: true,
    imports: [RouterLink, ConfirmDialogDirective, SpinnerComponent],
    templateUrl: './contract-organisations-tab.component.html',
    styleUrl: './contract-organisations-tab.component.scss'
})
export class ContractOrganisationsTabComponent extends LinkedRecordsTabBase<ContractLinkedOrganisation> implements OnChanges {
    @Input() contract!: Contract;

    protected readonly entityLabel = 'Organisation';

    private readonly contractsService = inject(ContractsService);
    private readonly authService = inject(AuthService);

    get isActive(): boolean {
        return !!this.contract && isContractActive(this.contract);
    }

    get canWrite(): boolean {
        return this.isActive && this.hasPermission(Permissions.MANAGE_CONTRACT_ORGANISATIONS);
    }

    get canViewOrganisation(): boolean {
        return this.hasPermission(Permissions.VIEW_ORGANISATIONS);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['contract']) {
            return;
        }
        const previous = changes['contract'].previousValue as Contract | undefined;
        const current = this.contract;
        // Reload on first render, a different contract, or an active/inactive flip.
        if (!previous || previous.id !== current?.id || isContractActive(previous) !== isContractActive(current)) {
            void this.load();
        }
    }

    rowId(row: ContractLinkedOrganisation): number {
        return row.id;
    }

    organisationLink(row: ContractLinkedOrganisation): string {
        return '/' + organisationDetailRoute(row.id);
    }

    /** Text marker (never colour alone) for a deactivated or deleted organisation. */
    organisationStatus(row: ContractLinkedOrganisation): string | null {
        if (row.deletedAt) {
            return 'Deleted';
        }
        return row.disabledAt ? 'Deactivated' : null;
    }

    protected async fetch(): Promise<ContractLinkedOrganisation[]> {
        if (!this.contract) {
            return [];
        }
        return (await this.contractsService.listOrganisations(this.contract.id, {linked: true})).data;
    }

    protected async unlinkOne(row: ContractLinkedOrganisation): Promise<void> {
        await this.contractsService.removeOrganisation(this.contract.id, row.id);
    }

    protected linkModalConfig(): LinkRecordsModalConfig<ContractLinkedOrganisation> {
        const contractId = this.contract.id;
        return {
            heading: 'Link organisations',
            titleId: 'link-contract-organisations-title',
            columns: [
                {header: 'Name', value: organisation => organisation.name},
                {header: 'Code', value: organisation => organisation.code},
                {header: 'City', value: organisation => organisation.city},
                {header: 'Postcode', value: organisation => organisation.postcode},
            ],
            loader: () => this.listAllCandidates(contractId),
            submitLabel: 'Add selected to contract',
            successMessage: 'Organisations linked to contract',
            emptyMessage: 'No organisations left to link',
            submit: async (ids: number[]) => {
                await this.contractsService.addOrganisations(contractId, ids);
            },
        };
    }

    /** Reads every page of the active organisations not yet linked (`linked=false`). */
    private async listAllCandidates(contractId: number): Promise<ContractLinkedOrganisation[]> {
        const all: ContractLinkedOrganisation[] = [];
        for (let page = 0; page < MAX_CANDIDATE_PAGES; page++) {
            const pagination = new Pagination();
            pagination.start = page;
            const response = await this.contractsService.listOrganisations(contractId, {linked: false}, pagination);
            const data = response?.data ?? [];
            all.push(...data);
            const totalPages = response?.pagination?.totalPages;
            if (data.length === 0 || (totalPages !== undefined && totalPages !== null && page + 1 >= totalPages)) {
                break;
            }
        }
        return all.filter(organisation => !organisation.disabledAt && !organisation.deletedAt);
    }

    private hasPermission(permission: Permissions): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[permission] ?? []).some(role => userRoles.includes(role));
    }
}
